import { useState } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Trans, useLingui } from "@lingui/react/macro"

import { SparkleIcon } from "./icons"
import { formatDuration } from "./call-duration"
import { RecordingPlayer } from "./recording-player"
import { useRecordingPlayback } from "@/lib/voice/recording-playback"
import type {
  TranscriptSegment,
  VoiceCallRecord,
} from "@/types/v2/voice"

// CON-159 — the dark "View transcript" sub-view inside the
// post-call card. Renders as content of the existing
// VoiceWidgetShell (which already paints surface #1E1B17 /
// radius 18 / shadow / drag handle), so this DOES NOT
// re-wrap in another dark card. Hardcoded hex is correct
// here: this is the floating call console, not a themed
// page surface.
interface AfterCallTranscriptProps {
  call: VoiceCallRecord
  onBack: () => void
}

// Deepgram labels channel 0 → "customer", 1 → "agent". The
// design shows the agent's turns as "You" (brand) and the
// customer's as the contact name (muted).
function speakerLabel(
  segment: TranscriptSegment,
  contactName: string | null,
  youLabel: string,
  customerFallback: string,
): string {
  if (segment.speaker === "agent") return youLabel
  if (segment.speaker === "customer") {
    return contactName?.trim() || customerFallback
  }
  return segment.speaker
}

export function AfterCallTranscript({
  call,
  onBack,
}: AfterCallTranscriptProps) {
  const { t } = useLingui()
  const playback = useRecordingPlayback(call.id, call.recordingPath)
  const turns = call.transcript ?? []
  const contactName = call.contact?.name ?? null

  // "Call with Ana · 4:32" when a contact name exists, else
  // "Call · 4:32". Duration prefers durationSeconds, then the
  // recording length; if neither is a usable number, the
  // "· {duration}" segment is dropped entirely (no NaN:NaN).
  const callTitle = (() => {
    const name = call.contact?.name?.trim()
    const base = name ? t`Call with ${name}` : t`Call`
    const seconds = call.durationSeconds ?? call.recordingDurationSeconds
    if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
      return base
    }
    return `${base} · ${formatDuration(seconds)}`
  })()

  // Tap a line to seek the recording to that turn's start
  // (drives the shared playback element).
  const seek = (seconds: number) => {
    playback.seek(seconds)
  }

  return (
    <div className="flex flex-col gap-3.5 px-[18px] pb-4 pt-[18px]">
      {/* Header: ‹ Back (brand) + Play-recording control */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label={t`Back`}
          data-testid="after-call-transcript-back"
          className="flex shrink-0 items-center gap-0.5 text-[13px] font-medium text-[#E0A56A] hover:underline"
        >
          <Trans>‹ Back</Trans>
        </button>
        <span className="min-w-0 grow truncate text-[13px] font-semibold text-[#FBF7F0]">
          {callTitle}
        </span>
        {call.recordingPath ? (
          <button
            type="button"
            onClick={() => seek(0)}
            data-testid="after-call-transcript-play"
            className="flex shrink-0 items-center gap-1.5 rounded-[10px] border border-[#3A352E] px-2.5 py-1 text-[13px] font-semibold text-[#E0A56A]"
          >
            <span aria-hidden className="text-[#FBF7F0]">
              ▶
            </span>
            <Trans>Play recording</Trans>
          </button>
        ) : null}
      </div>

      {/* The real recorder (play + scrubber) the turn taps
          drive via the imperative `seek`. Hidden when there's
          no recording. */}
      {call.recordingPath ? (
        <RecordingPlayer
          voiceCallId={call.id}
          src={call.recordingPath}
          durationSeconds={
            call.recordingDurationSeconds ?? call.durationSeconds
          }
          variant="study"
        />
      ) : null}

      {/* AI summary pin — omitted entirely when null */}
      {call.summary ? (
        <div className="flex flex-col gap-[7px] rounded-[12px] bg-[#2A2620] px-[15px] py-[13px]">
          <div className="flex items-center gap-1.5">
            <SparkleIcon
              width={11}
              height={11}
              className="text-[#E0A56A]"
            />
            <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#E0A56A]">
              <Trans>AI summary</Trans>
            </span>
          </div>
          <p className="text-[13px] leading-[19px] text-[#D9D3C8]">
            {call.summary}
          </p>
        </div>
      ) : null}

      <TranscriptTurns
        turns={turns}
        contactName={contactName}
        youLabel={t`You`}
        customerFallback={t`Customer`}
        onSeek={seek}
      />

      {turns.length > 0 ? (
        <p className="text-[12px] leading-[19px] text-[#6E685F]">
          <Trans>Auto-transcribed · tap a line to seek</Trans>
        </p>
      ) : null}
    </div>
  )
}

interface TranscriptTurnsProps {
  turns: TranscriptSegment[]
  contactName: string | null
  youLabel: string
  customerFallback: string
  onSeek: (seconds: number) => void
}

// Virtualized turn list (worst-case long calls). Callback-ref
// state — not useRef — so tanstack-virtual sees a real element
// on first paint. ResizeObserver is polyfilled in jsdom by
// the test setup.
function TranscriptTurns({
  turns,
  contactName,
  youLabel,
  customerFallback,
  onSeek,
}: TranscriptTurnsProps) {
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null)

  const virtualizer = useVirtualizer({
    count: turns.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => 64,
    overscan: 6,
  })

  if (turns.length === 0) return null

  return (
    <div
      ref={setScrollEl}
      data-testid="after-call-transcript-turns"
      className="max-h-[360px] overflow-y-auto overflow-x-hidden pt-0.5"
    >
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const turn = turns[item.index]
          if (!turn) return null
          const isAgent = turn.speaker === "agent"
          return (
            <div
              key={item.key}
              data-index={item.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              <button
                type="button"
                onClick={() => onSeek(turn.start)}
                className="flex w-full gap-3 pb-3.5 text-left"
              >
                <span className="w-[42px] shrink-0 text-[11px] text-[#6E685F]">
                  {formatDuration(turn.start)}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span
                    className={`text-[12px] font-semibold leading-[14px] ${
                      isAgent ? "text-[#E0A56A]" : "text-[#9A938A]"
                    }`}
                  >
                    {speakerLabel(turn, contactName, youLabel, customerFallback)}
                  </span>
                  <span className="min-w-0 break-words text-[13px] leading-[19px] text-[#FBF7F0]">
                    {turn.text}
                  </span>
                </span>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
